import { useEffect, useRef } from 'react';
import { montarCaptcha } from '../services/captcha.js';

/**
 * El widget de Turnstile, `appearance: 'interaction-only'`: normalmente no ocupa espacio ni se ve;
 * si Cloudflare decide que hace falta un clic, aparece aquí mismo, dentro del formulario (2026-09-28:
 * antes vivía en un `<div>` fuera de pantalla y ese reto no se podía ver ni pulsar nunca).
 *
 * Se monta al aparecer este componente, no al enviar el formulario, para que el token esté listo
 * (o el reto ya resuelto) antes de que la persona pulse el botón la mayoría de las veces.
 *
 * `onCambio(token)` se llama con el token en cuanto lo hay, y con `null` si aún no lo hay, si
 * expira o si Cloudflare da un error. Sin `VITE_TURNSTILE_SITE_KEY` no se renderiza nada.
 */
export function CaptchaWidget({ onCambio }: { readonly onCambio: (token: string | null) => void }) {
  const contenedorRef = useRef<HTMLDivElement>(null);
  // El efecto se monta una sola vez (contenedor fijo durante toda la vida del widget); el ref
  // deja llamar siempre a la versión más reciente de `onCambio` sin tener que remontar el widget
  // cada vez que el componente que lo usa se renderiza de nuevo.
  const onCambioRef = useRef(onCambio);
  onCambioRef.current = onCambio;

  useEffect(() => {
    const contenedor = contenedorRef.current;
    if (contenedor === null) return undefined;
    return montarCaptcha(contenedor, token => {
      onCambioRef.current(token);
    });
  }, []);

  return <div ref={contenedorRef} />;
}
